import { useState, useCallback } from "react";
import { Bluelagoon, useTaskWebSocket } from "@bluelagoon";

export function useEndpointFix({ addChat, onFixApplied }) {
    const [fixTaskId, setFixTaskId] = useState(null);
    const [isFixing, setIsFixing] = useState(false);

    const handleFixUpdate = useCallback((update) => {
        const data = update.data || update;
        const feedback = data.partialFeedback || [];

        // Process chat messages from the fix agent
        for (const entry of feedback) {
            if (entry?.type === "chat" && entry.text) {
                addChat({ role: "ai", text: entry.text });
            }
            if (entry?.type === "fix_complete" && entry.data) {
                setIsFixing(false);
                setFixTaskId(null);
                onFixApplied(entry.data);
            }
        }

        if (data.status === "COMPLETED") {
            setIsFixing(false);
            setFixTaskId(null);
            const fixedIntegration = data.integration || data.output?.integration;
            if (fixedIntegration) {
                onFixApplied(fixedIntegration);
            } else {
                addChat({ role: "ai", text: "AI could not determine a fix. Try editing the path manually." });
            }
        } else if (data.status === "FAILED" || data.status === "CANCELLED") {
            setIsFixing(false);
            setFixTaskId(null);
            addChat({ role: "ai", text: "Fix attempt failed. Try editing the path manually." });
        }
    }, [addChat, onFixApplied]);

    useTaskWebSocket({ taskId: fixTaskId, onUpdate: handleFixUpdate, enabled: !!fixTaskId });

    const requestFix = useCallback(async ({ endpoint, error, integration, message, credentials }) => {
        setIsFixing(true);
        addChat({ role: "ai", text: `Asking AI to fix **${endpoint.method} ${endpoint.path}**...` });
        try {
            const res = await Bluelagoon.fixIntegrationEndpoint({ endpoint, error, integration, message, credentials });
            const body = res?.data?.data || res?.data;
            if (body?.taskId) {
                setFixTaskId(body.taskId);
            } else {
                setIsFixing(false);
                addChat({ role: "ai", text: body?.message || "Failed to start fix." });
            }
        } catch (err) {
            setIsFixing(false);
            addChat({ role: "ai", text: `Error: ${err.message}` });
        }
    }, [addChat]);

    return { isFixing, requestFix };
}
